import type { IdentityPrepareResult } from '@repo/api-contracts';
import { useState } from 'react';
import { trpc } from '@/shared/api';
import { env } from '@/shared/config';
import { getErrorMessage } from '@/shared/lib';
import { encodeMockResult, type MockVerificationResult } from './mock-result';

/** Mock 어댑터의 authUrl 접두사 — 이 값이면 실제 인증창이 없는 개발 모드다. */
const MOCK_AUTH_URL_PREFIX = 'mock://';

interface UseIdentityVerificationResult {
  readonly prepared: IdentityPrepareResult | null;
  readonly isMock: boolean;
  readonly error: string | null;
  readonly isPending: boolean;
  /** 인증창 요청 데이터를 발급받는다. (5회/시간 제한) */
  readonly prepare: () => Promise<void>;
  /** 실제 대행사 모드 — 발급받은 파라미터로 인증창을 새 창에 연다. */
  readonly openAuthWindow: () => void;
  /** Mock 모드 — 가짜 결과를 서버 콜백에 직접 보내 인증을 완료한다. (개발 전용) */
  readonly completeMock: (input: Omit<MockVerificationResult, 'transactionId'>) => Promise<boolean>;
}

/** PASS 본인인증 흐름. (설계 §2-4 — prepare → 인증창 → 서버 콜백) */
export const useIdentityVerification = (): UseIdentityVerificationResult => {
  const [prepared, setPrepared] = useState<IdentityPrepareResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const mutation = trpc.identity.prepare.useMutation();

  const prepare = async (): Promise<void> => {
    setError(null);

    try {
      setPrepared(await mutation.mutateAsync());
    } catch (caught) {
      setError(getErrorMessage(caught, '본인인증 준비에 실패했습니다.'));
    }
  };

  const openAuthWindow = (): void => {
    if (!prepared || prepared.authUrl.startsWith(MOCK_AUTH_URL_PREFIX)) {
      return;
    }

    // 대행사 인증창은 파라미터를 POST 로 받는다 — 폼을 만들어 새 창으로 제출한다.
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = prepared.authUrl;
    form.target = '_blank';
    // 제3자 창이 window.opener 로 원래 페이지를 조작(오프너 하이재킹)하지 못하게 한다.
    form.rel = 'noopener noreferrer';

    for (const [name, value] of Object.entries(prepared.formFields)) {
      const field = document.createElement('input');
      field.type = 'hidden';
      field.name = name;
      field.value = value;
      form.appendChild(field);
    }

    document.body.appendChild(form);
    form.submit();
    form.remove();
  };

  const completeMock = async (
    input: Omit<MockVerificationResult, 'transactionId'>,
  ): Promise<boolean> => {
    if (!prepared) {
      return false;
    }

    setError(null);

    try {
      // 콜백은 대행사 서버가 호출하는 공개 웹훅이다 — mock 모드에서는 브라우저가 대신한다.
      const response = await fetch(`${env.VITE_API_URL}/api/identity/callback`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          encodedResult: encodeMockResult({ ...input, transactionId: prepared.transactionId }),
        }),
      });

      if (!response.ok) {
        const body = (await response.json()) as { error?: { message?: string } | null };
        setError(body.error?.message ?? '본인인증에 실패했습니다.');
        // 서버가 거부한 시점에는 거래 키가 이미 소비됐을 수 있다 — 같은 거래 ID 로
        // 재제출하면 무조건 "유효하지 않은 인증 요청" 이 되므로 처음부터 다시 시작시킨다.
        // (네트워크 오류는 키가 살아 있으므로 아래 catch 에서 유지한다 — prepare 는 5회/시간)
        setPrepared(null);
        return false;
      }

      return true;
    } catch (caught) {
      setError(getErrorMessage(caught, '본인인증에 실패했습니다.'));
      return false;
    }
  };

  return {
    prepared,
    isMock: prepared?.authUrl.startsWith(MOCK_AUTH_URL_PREFIX) ?? false,
    error,
    isPending: mutation.isPending,
    prepare,
    openAuthWindow,
    completeMock,
  };
};
